import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BseDto,
  ContractTemplateDto,
  ReceivedBseBidDto,
  useCreateContractFromSourcingEventMutation,
  useListContractTemplatesQuery,
  useListTextDocumentsForSourcingEventQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { SelectorValue } from "../../../../common/input/Selector/SelectorValue";
import { SingleFormSelector } from "../../../../common/input/Selector/SingleFormSelector";
import { ModalSkeleton } from "../../../../common/ModalSkeleton";
import { urls } from "../../../../urls";

interface Props {
  sourcingEvent: BseDto;
  bid: ReceivedBseBidDto;
  onClose: () => void;
}

interface FormValues {
  title: string;
  template: SelectorValue | null;
}

const SelectableDocumentGroup = ({
  label,
  documents,
  selectedIds,
  onToggle,
}: {
  label: string;
  documents: { id: string; name: string }[];
  selectedIds: string[];
  onToggle: (id: string, checked: boolean) => void;
}) => {
  if (documents.length === 0) return null;
  return (
    <Stack spacing="1">
      <Text fontSize="sm" fontWeight="semibold" color="smText">
        {label}
      </Text>
      {documents.map((doc) => (
        <Checkbox
          key={doc.id}
          isChecked={selectedIds.includes(doc.id)}
          onChange={(e) => onToggle(doc.id, e.target.checked)}
        >
          <Text as="span" color="smText" fontSize="sm">
            {doc.name}
          </Text>
        </Checkbox>
      ))}
    </Stack>
  );
};

export const CreateContractModal = ({ sourcingEvent, bid, onClose }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger, triggerSoft } = useApiError();

  const [createContract, { isLoading: isCreatingContract }] = useCreateContractFromSourcingEventMutation();

  const bidDocuments = bid.documents ?? [];
  const eventDocuments = sourcingEvent.documents ?? [];

  const {
    data: eventTextDocumentsRes,
    isLoading: isLoadingEventTextDocuments,
    error: eventTextDocumentsError,
  } = useListTextDocumentsForSourcingEventQuery({ eventId: sourcingEvent.id });
  const eventTextDocuments = eventTextDocumentsRes?.documents ?? [];

  useEffect(() => {
    if (eventTextDocumentsError) {
      triggerSoft(eventTextDocumentsError);
    }
  }, [eventTextDocumentsError, triggerSoft]);

  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>(() => [
    ...new Set([...bidDocuments, ...eventDocuments].map((d) => d.id)),
  ]);
  const toggleDocument = (id: string, checked: boolean) =>
    setSelectedDocumentIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((d) => d !== id)));

  const [textDocumentSelection, setTextDocumentSelection] = useState<string[] | null>(null);
  const selectedTextDocumentIds = textDocumentSelection ?? eventTextDocuments.map((d) => d.id);
  const toggleTextDocument = (id: string, checked: boolean) =>
    setTextDocumentSelection(
      checked ? [...new Set([...selectedTextDocumentIds, id])] : selectedTextDocumentIds.filter((d) => d !== id)
    );

  const hasSelectableDocuments = bidDocuments.length + eventDocuments.length + eventTextDocuments.length > 0;

  const { data: templatesRes, isLoading: isLoadingTemplates } = useListContractTemplatesQuery({
    organizationId: authState.selectedOrg.id,
    orgId: authState.selectedOrg.id,
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>();

  const getTemplateName = (template: ContractTemplateDto): string => {
    if (template.name) {
      return template.name;
    } else if (template.commonType) {
      switch (template.commonType) {
        case "ExpirationDate":
          return t("Contract with expiration date");
      }
    }
    throw new Error("Invalid template");
  };

  const submit = async (values: FormValues) => {
    try {
      const createResult = await createContract({
        createContractFromSourcingEventRequest: {
          eventId: sourcingEvent.id,
          bidId: bid.id,
          title: values.title,
          templateId: values.template?.value ?? undefined,
          documentIds: selectedDocumentIds,
          textDocumentIds: selectedTextDocumentIds,
        },
      }).unwrap();

      setValue("title", "");
      setValue("template", null);

      toast({
        title: t("Contract created successfully"),
        status: "success",
        position: "top-right",
      });

      navigate(urls.contracts.edit.go(createResult.id, sourcingEvent.id).details);
    } catch (error) {
      trigger(error);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("New contract with")} {bid.owningOrganization.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoadingTemplates || isLoadingEventTextDocuments ? (
            <ModalSkeleton />
          ) : (
            <form id="create-contract-form" onSubmit={handleSubmit(submit)}>
              <FormControl isInvalid={errors.title !== undefined} pt="2.5">
                <FormLabel htmlFor={"title"}>{t("Contract title")}</FormLabel>
                <Input
                  id={"title"}
                  type="title"
                  backgroundColor="smBackground"
                  {...register("title", {
                    required: t("Please provide a title for your contract") ?? "",
                    minLength: {
                      value: 2,
                      message: t("Must be at least 2 characters long"),
                    },
                  })}
                />
                <FormErrorMessage>{errors.title && errors.title.message}</FormErrorMessage>
                <FormHelperText>{t("Please provide a title for your contract")}</FormHelperText>
              </FormControl>
              <Flex pt="4">
                {templatesRes && (
                  <SingleFormSelector
                    options={templatesRes.templates.map((t) => ({
                      label: getTemplateName(t),
                      value: t.id,
                    }))}
                    name={"template"}
                    label={t("Select type")}
                    helperText={t("Select a type") ?? ""}
                    control={control}
                    placeholderText={""}
                    noOptionsProvidedMessage={t("No types provided")}
                    noMatchingOptionsMessage={t("No matching types")}
                    errorMessage={undefined}
                  />
                )}
              </Flex>
              <FormControl pt="4">
                <FormLabel>{t("Documents to copy to the contract")}</FormLabel>
                {hasSelectableDocuments ? (
                  <>
                    <Stack spacing="4" maxH="80" overflowY="auto" pr="2">
                      <SelectableDocumentGroup
                        label={t("Bid documents")}
                        documents={bidDocuments}
                        selectedIds={selectedDocumentIds}
                        onToggle={toggleDocument}
                      />
                      <SelectableDocumentGroup
                        label={t("Documents from the procurement")}
                        documents={eventDocuments}
                        selectedIds={selectedDocumentIds}
                        onToggle={toggleDocument}
                      />
                      <SelectableDocumentGroup
                        label={t("Text documents from the procurement")}
                        documents={eventTextDocuments}
                        selectedIds={selectedTextDocumentIds}
                        onToggle={toggleTextDocument}
                      />
                    </Stack>
                    <FormHelperText>{t("Selected documents are copied onto the new contract.")}</FormHelperText>
                  </>
                ) : (
                  <Text color="smMuted" fontSize="sm">
                    {t("There are no documents to copy. The contract can be created without attachments.")}
                  </Text>
                )}
              </FormControl>
              <Flex width="full" justify="end">
                <Button
                  type="submit"
                  form="create-contract-form"
                  colorScheme="teal"
                  isLoading={isCreatingContract}
                  mt="8"
                >
                  {t("Create contract")}
                </Button>
              </Flex>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
